import { useEffect, useState } from "react";
import { create } from "zustand";

interface TickerStore {
  tick: number;
}

const useTickerStore = create<TickerStore>(() => ({ tick: 0 }));

/**
 * JEDEN globální setInterval pro celou aplikaci (default 500ms).
 * Subscribery jen čtou hodnotu přes `useTicker()` selector.
 *
 * Důvod: původní `useTopics` vytvářel N samostatných setIntervalů (jeden na topic),
 * což pro 30 topiců = 60 ticků/s jen na výpočet `suspicious` flagů. Centralizovaný
 * ticker → 2 ticky/s celkem.
 */
let globalTimer: ReturnType<typeof setInterval> | null = null;
let subscriberCount = 0;

const startGlobalTimer = () => {
  if (globalTimer) return;
  globalTimer = setInterval(() => {
    useTickerStore.setState((s) => ({ tick: s.tick + 1 }));
  }, 500);
};

const stopGlobalTimer = () => {
  if (!globalTimer) return;
  clearInterval(globalTimer);
  globalTimer = null;
};

/**
 * Hook subscribuje aktuální tick (rerender každých 500ms pokud komponenta aktivní).
 * Reference-counting: globální timer běží jen pokud existuje alespoň 1 subscriber.
 */
export const useTicker = (): number => {
  const tick = useTickerStore((s) => s.tick);

  useEffect(() => {
    subscriberCount += 1;
    if (subscriberCount === 1) startGlobalTimer();
    return () => {
      subscriberCount -= 1;
      if (subscriberCount === 0) stopGlobalTimer();
    };
  }, []);

  return tick;
};

/** Pro ne-React code (např. cleanup pří shutdown). */
export const _resetTicker = () => {
  stopGlobalTimer();
  subscriberCount = 0;
  useTickerStore.setState({ tick: 0 });
};

// Suppress unused warning for testing helper
void useState;
